import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import RoutineStyle from '../../../../components/Routine&Performance/RoutineUpload.module.css';

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    styled
} from '@mui/material';

import ProcessMapUploadForm from '../../Forms/ProcessMapUploadForm/ProcessMapUploadForm';
import { X } from 'lucide-react';
import { apiService } from '../../../../api/apiService';
import SuccessPopup from '../../Popup/Success&ErrorPopup/SuccessPopup';
import ErrorPopup from '../../Popup/Success&ErrorPopup/ErrorPopup';
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';


interface ProcessFile {
    id: string;
    name: string;
    status: string;
    size: string;
}

interface ChangeManagementPopupProps {
    openUpload: boolean;
    setOpenUpload: Dispatch<SetStateAction<boolean>>;
    management: string;
    verticalTabValue?: string;
    verticalTabValueTitle?: string;
    onSuccess?: () => void;

}

const StyledMenuItem = styled(MenuItem)({
    color: 'black', // Set the text color to black
    '&:hover': {
        backgroundColor: '#f0f0f0', // Optional: hover background color
    },
});

const TalentGrowthPopup: React.FC<ChangeManagementPopupProps> = ({ openUpload, setOpenUpload, management, verticalTabValue, verticalTabValueTitle, onSuccess }) => {
    const { instance } = useMsal();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [files, setFiles] = useState<ProcessFile[]>([]);
   const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');    const [uploading, setUploading] = useState<boolean>(false);

    // Mapping for file titles based on verticalTabValueTitle
    const fileTitleMapping = {
        a: "Pipeline health is monitored in the MCRS up until granular level.",
        b: "Collect and analyze exit interview data to identify the main reasons for employee departures. Ensure managers are provided with insights into these reasons and regularly discuss them in team meetings or with PBPs.  Develop a targeted action plan to address key attrition reasons.",
    };

    // Get the file title based on verticalTabValueTitle, or use a default value if not found
    const fileTitle = verticalTabValueTitle ? fileTitleMapping[verticalTabValueTitle as keyof typeof fileTitleMapping] : "Upload the file";
    console.log("File title:", fileTitle);

    const handleFileSelect = (file: File) => {
        setSelectedFile(file);
    };

    const handleSubmit = async () => {
        if (selectedFile) {
            const formData = new FormData();
            const processMapReq = {
                section_name: verticalTabValue,
                management: management,
            };

            formData.append("talent_n_growth", JSON.stringify(processMapReq));
            formData.append("file", selectedFile);

            console.log("Payload:", Array.from(formData.entries()));
            setUploading(true);

            try {
                const result = await apiService("/people/talent_n_growth", "POST", formData, undefined, instance as PublicClientApplication);
                console.log("Submission successful:", result);

                setFiles([
                    {
                        id: Math.random().toString(36).substr(2, 9),
                        name: selectedFile.name,
                        status: "Submitted",
                        size: `${Math.round(selectedFile.size / 1024)} KB`,
                    },
                    ...files,
                ]);

                setOpenUpload(false);
                resetUploadState();
                setShowSuccess(true);
                if (onSuccess) {
                    onSuccess(); // Call the callback function after successful upload
                }
            } catch (error) {
                console.error("Error during submission:", error);
            }finally{
                setUploading(false);
            }
        }
    };

    const resetUploadState = () => {
        setSelectedFile(null);
        setUploadProgress(0);
    };

       useEffect(() => {
            let timer: NodeJS.Timeout;
            if (showSuccess) {
                timer = setTimeout(() => {
                    setShowSuccess(false);
                }, 3000);
            }
            return () => clearTimeout(timer);
        }, [showSuccess]);
    
        useEffect(() => {
            let timer: NodeJS.Timeout;
            if (showError) {
                timer = setTimeout(() => {
                    setShowError(false);
                }, 3000);
            }
            return () => clearTimeout(timer);
        }, [showError]);

    return (

        <>
        <Dialog
            open={openUpload}
            onClose={() => {
                setOpenUpload(false);
                resetUploadState();
            }}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#000' }}>
                {verticalTabValue}
                <IconButton onClick={() => setOpenUpload(false)}>
                    <X size={20} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <ProcessMapUploadForm
                    file={selectedFile}
                    onFileSelect={handleFileSelect}
                    onFileRemove={() => {
                        setSelectedFile(null);
                        setUploadProgress(0);
                    }}
                    fileTitle={fileTitle} // Use the dynamically determined file title
                    uploadProgress={uploadProgress}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button variant='outlined' onClick={() => setOpenUpload(false)} sx={{ color: '#000', borderColor: '#000' }}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!selectedFile || uploading}
                    sx={{
                        color: 'white',
                        bgcolor: 'black',
                        '&:hover': { bgcolor: 'black' },
                        '&:disabled': { bgcolor: 'grey.300' },
                    }}
                >
              {uploading ? 'Uploading...' : 'Submit'}
                </Button>
            </DialogActions>
        </Dialog>

         {/* Success Popup */}
         <SuccessPopup
                open={showSuccess}
                onClose={() => setShowSuccess(false)}
                message={`${verticalTabValue} uploaded successfully`}
            />

            {/* Error Popup */}
            <ErrorPopup
                open={showError}
                onClose={() => setShowError(false)}
                message={errorMessage}
            />

            </>
    );
};

export default TalentGrowthPopup;